import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

type PullState = 'idle' | 'pulling' | 'refreshing';

interface UsePullToRefreshOptions {
  threshold?: number;
  maxPull?: number;
}

interface UsePullToRefreshReturn {
  pullDistance: number;
  isRefreshing: boolean;
  state: PullState;
}

export function usePullToRefresh(
  scrollContainerRef: RefObject<HTMLElement | null>,
  onRefresh: () => Promise<void>,
  options?: UsePullToRefreshOptions,
): UsePullToRefreshReturn {
  const threshold = options?.threshold ?? 60;
  const maxPull = options?.maxPull ?? 120;

  const [pullDistance, setPullDistance] = useState(0);
  const [state, setState] = useState<PullState>('idle');

  const startYRef = useRef(0);
  const pullingRef = useRef(false);
  const hapticFiredRef = useRef(false);
  const rafRef = useRef<number>(0);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (state === 'refreshing') return;
      const container = scrollContainerRef.current;
      if (!container || container.scrollTop > 0) return;

      startYRef.current = e.touches[0].clientY;
      pullingRef.current = false;
      hapticFiredRef.current = false;
    },
    [state, scrollContainerRef],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (state === 'refreshing') return;
      const container = scrollContainerRef.current;
      if (!container) return;

      const currentY = e.touches[0].clientY;
      const rawDistance = currentY - startYRef.current;

      if (rawDistance <= 0 || container.scrollTop > 0) {
        if (pullingRef.current) {
          pullingRef.current = false;
          setPullDistance(0);
          setState('idle');
        }
        return;
      }

      e.preventDefault();
      pullingRef.current = true;

      const dampened = Math.min(rawDistance * 0.5, maxPull);

      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setPullDistance(dampened);
        setState('pulling');
      });

      if (dampened >= threshold && !hapticFiredRef.current) {
        hapticFiredRef.current = true;
        if (Capacitor.isNativePlatform()) {
          Haptics.impact({ style: ImpactStyle.Medium });
        }
      }
      if (dampened < threshold && hapticFiredRef.current) {
        hapticFiredRef.current = false;
      }
    },
    [state, scrollContainerRef, threshold, maxPull],
  );

  const handleTouchEnd = useCallback(async () => {
    if (!pullingRef.current || state === 'refreshing') {
      return;
    }

    pullingRef.current = false;
    cancelAnimationFrame(rafRef.current);

    if (pullDistance >= threshold) {
      setState('refreshing');
      setPullDistance(threshold);
      try {
        await onRefresh();
      } finally {
        setState('idle');
        setPullDistance(0);
      }
    } else {
      setState('idle');
      setPullDistance(0);
    }
  }, [pullDistance, threshold, state, onRefresh]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      cancelAnimationFrame(rafRef.current);
    };
  }, [scrollContainerRef, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { pullDistance, isRefreshing: state === 'refreshing', state };
}
